import React, { useState } from 'react';
import {
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { Loader2 } from 'lucide-react';
import { formatFinnishCurrency } from '@/utils/Currency';

interface StripeCheckoutFormProps {
  transactionId: string;
  amount: number;
  returnUrl?: string;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

const StripeCheckoutForm: React.FC<StripeCheckoutFormProps> = ({
  transactionId,
  amount,
  returnUrl,
  onSuccess,
  onError,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setErrorMessage(null);

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment/success?transaction_id=${transactionId}${returnUrl ? `&returnUrl=${encodeURIComponent(returnUrl)}` : ''}`,
        },
      });

      if (error) {
        setErrorMessage(error.message || 'An error occurred during payment');
        onError?.(error.message || 'Payment failed');
      } else {
        onSuccess?.();
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Payment failed';
      setErrorMessage(errorMsg);
      onError?.(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" aria-busy={processing}>
      <div className="card border rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Payment Details
        </h3>

        <PaymentElement
          options={{
            layout: 'tabs',
            defaultValues: {
              billingDetails: {
                address: {
                  country: 'FI',
                },
              },
            },
          }}
        />
      </div>

      {errorMessage && (
        <div
          className="bg-red-50 dark:bg-red-950 contrast:bg-red-950 border border-red-200 dark:border-red-900 contrast:border-red-900 rounded-lg p-4"
          role="alert"
          aria-live="polite"
        >
          <p className="text-sm text-red-600 dark:text-red-200 contrast:text-red-200">
            {errorMessage}
          </p>
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || processing}
        className={`w-full py-3 px-4 rounded-lg font-semibold transition-all ${
          !stripe || processing
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-md hover:shadow-lg'
        }`}
      >
        {processing ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
            Processing Payment...
          </span>
        ) : (
          `Pay ${formatFinnishCurrency(amount)}`
        )}
      </button>

      <p className="text-xs text-gray-500 text-center">
        Your payment is secured by Stripe
      </p>
    </form>
  );
};

export default StripeCheckoutForm;
